import fulfill from '../../fulfill';
import { SideEffectFunction } from '../../sideEffects';
import { CHECKOUT_API_HOSTNAME } from '../../env';
import { CheckoutConfig, Transaction } from '../../types/checkout';
import {
    ActionTypes,
    FetchTransactionsResponse,
    GetTransactions,
    FetchNextResponse,
    GetTransactionReason,
    GetFilterData,
    FetchFilterDataResponse
} from './actions';
import { SalesLocation } from '../../types/customers';
import { recursivelyFetchLocations } from '../salesLocationsFetcher';
import { isDigit } from '../../helpers/validation/digit';

const rewriteSearchParam = (search: string) => {
    // Strip phone number formatting separators from search param
    const withoutPhoneNumberFormatting = search.replace(/\s/g, '').replace(/-/g, '');
    const isPhoneNumberLike = withoutPhoneNumberFormatting.startsWith('+') && isDigit(withoutPhoneNumberFormatting.substring(1)) && withoutPhoneNumberFormatting.length > 3;
    if (isPhoneNumberLike) {
        return withoutPhoneNumberFormatting;
    }
    return search;
};

const preprocessParams = (params: URLSearchParams) => {
    // Rewrite search params before server-side processing
    if (params.get('search')) {
        params.set('search', rewriteSearchParam(params.get('search') as string));
    }
    return params;
};

const fetchTransactions: SideEffectFunction<GetTransactions, FetchTransactionsResponse> = async (action) => {
    const params = preprocessParams(action.payload.params);
    const result: FetchTransactionsResponse = await fulfill.get({
        accountId: action.payload.accountId,
        url: `${CHECKOUT_API_HOSTNAME}/v1/transactions?${params.toString()}`,
        handlers: {
            200: (transactions: Transaction[]) => ({
                type: ActionTypes.FetchTransactionsResponse,
                payload: {
                    transactions: transactions,
                },
                meta: {
                    cause: action,
                },
            }),
            400: () => ({
                type: ActionTypes.FetchTransactionsResponse,
                payload: {
                    transactions: [],
                },
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

const fetchNext: SideEffectFunction<FetchTransactionsResponse, FetchNextResponse | undefined> = async (action) => {
    if (action.meta.cause.payload.reason === GetTransactionReason.refresh) {
        // no need to check next page if the causeing action was a "refresh"
        return;
    }
    const hasLimitInParams = action.meta.cause.payload.params.get('limit');
    const isAtLastPage = hasLimitInParams
        ? action.payload.transactions.length < parseInt(hasLimitInParams)
        : action.payload.transactions.length === 0;
    if (isAtLastPage) {
        return {
            type: ActionTypes.FetchNextResponse,
            payload: {
                transactions: [],
            },
            meta: {
                cause: action,
            },
        };
    }
    const params = preprocessParams(action.meta.cause.payload.params);
    const lastTransaction = action.payload.transactions[action.payload.transactions.length - 1];
    params.delete('starting_after');
    if (lastTransaction && lastTransaction.id) {
        params.append('starting_after', lastTransaction.id);
    }
    params.delete('limit');
    params.append('limit', '1');
    const result: FetchNextResponse = await fulfill.get({
        accountId: action.meta.cause.payload.accountId,
        url: `${CHECKOUT_API_HOSTNAME}/v1/transactions?${params.toString()}`,
        handlers: {
            200: (transactions: Transaction[]) => ({
                type: ActionTypes.FetchNextResponse,
                payload: {
                    transactions: transactions,
                },
                meta: {
                    cause: action,
                },
            }),
        },
    });
    return result;
};

const fetchCheckoutConfigurations = async (accountIds: string[]) => {
    const promises = accountIds.map((accountId) =>
        fulfill.get({
            accountId: accountId,
            url: `${CHECKOUT_API_HOSTNAME}/v1/admin/checkout`,
            handlers: {
                200: (configuration: CheckoutConfig[]) => ({
                    configuration,
                    accountId,
                }),
                404: () => ({
                    configuration: undefined,
                    accountId,
                }),
            },
        }));
    const results = await Promise.all(promises);
    return results.reduce((acc, result) => {
        if (!result || !result.configuration) {
            return acc;
        }
        return {
            ...acc,
            [result.accountId]: result.configuration,
        };
    }, {});
};

const fetchSalesLocations = async (accountIds: string[], fetchLocations: boolean) => {
    const promises = accountIds.map(async (accountId) => {
        let locations: SalesLocation[] = [];
        if (fetchLocations) {
            locations = await recursivelyFetchLocations(
                accountId,
                new URLSearchParams([['limit', '100']]),
                []
            );
        }
        return { accountId, locations };
    });
    const results = await Promise.all(promises);
    return results.reduce((acc, result) => {
        return {
            ...acc,
            [result.accountId]: result.locations,
        };
    }, {});
};

const fetchFilterData: SideEffectFunction<
    GetFilterData,
    FetchFilterDataResponse
> = async (action) => {
    const { accountIds, fetchLocations } = action.payload;
    const configurations = await fetchCheckoutConfigurations(accountIds);
    const locations = await fetchSalesLocations(accountIds, fetchLocations);
    return {
        type: ActionTypes.FetchFilterDataResponse,
        payload: {
            configurations,
            locations,
        },
        meta: {
            cause: action,
        },
    };
};

const effects = {
    [ActionTypes.GetTransactions]: fetchTransactions,
    [ActionTypes.FetchTransactionsResponse]: fetchNext,
    [ActionTypes.GetFilterData]: fetchFilterData,
};

export default effects;
